import { Link } from "react-router-dom";
import { ShieldCheck, Clock, MapPin, Users, BadgeCheck, DollarSign, ArrowRight, CheckCircle2, Phone, Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HERO, WHY_US, BENEFITS, FAQ_ITEMS, COMPANY, COURSE } from "@/data/mockData";
import { useUpcomingCourses } from "@/hooks/useUpcomingCourses";
import { useState } from "react";
import ToastHelper from "@/utils/toastHelper";

const ICONS = { ShieldCheck, Clock, MapPin, Users, BadgeCheck, DollarSign };

export default function Home() {
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const submit = (e) => {
    e.preventDefault();
    ToastHelper.success("Thanks - we'll be in touch within 1 business hour.");
    setContact({ name: "", email: "", phone: "", message: "" });
  };
  const { courses, loading: coursesLoading, error: coursesError } = useUpcomingCourses();
  const upcoming = courses.slice(0, 4);
  const featuredCourse = upcoming[0] || courses[0] || null;

  const formatTime = (value) => {
    if (!value) return "Time TBA";
    const text = String(value);
    const parts = text.split(":");
    if (parts.length < 2) return text;
    const date = new Date();
    date.setHours(Number(parts[0]), Number(parts[1]), 0, 0);
    return date.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
  };

  const courseTitle = `${COURSE.code} - White Card`;
  const courseFee = COURSE.fee;
  const courseDescription = COURSE.name;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-slate-50" data-testid="hero-section">
        <div className="absolute inset-0 grain-texture opacity-[0.04] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div className="animate-fadeInUp">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-700 rounded-full px-3 py-1 text-xs font-semibold mb-6">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
              {HERO.eyebrow}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold text-[#0A2540] tracking-tight leading-[1.05]">
              {HERO.title}<span className="text-orange-500">.</span>
            </h1>
            <p className="mt-4 text-xl text-slate-600 font-display font-semibold">{HERO.subtitle}</p>
            <ul className="mt-8 space-y-3">
              {HERO.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" /><span>{b}</span></li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/calendar" className="wc-btn-primary text-base" data-testid="hero-book-now"><span>Book Now</span><ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="hidden  mt-8 flex items-center gap-6 pt-6 border-t border-slate-200">
              <div><div className="text-2xl font-display font-extrabold text-[#0A2540]">4.9<span className="text-orange-500">★</span></div><div className="text-xs text-slate-500">1,200+ reviews</div></div>
              <div><div className="text-2xl font-display font-extrabold text-[#0A2540]">2,800+</div><div className="text-xs text-slate-500">Students trained</div></div>
              <div><div className="text-2xl font-display font-extrabold text-[#0A2540]">97%</div><div className="text-xs text-slate-500">Same-day pass rate</div></div>
            </div>
          </div>

          <div className="relative animate-fadeInUp" style={{ animationDelay: "150ms" }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 aspect-[4/5]">
              <img src={HERO.heroImage} alt="Construction worker" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/50 to-transparent" />
            </div>
            <div className="hidden lg:block absolute -bottom-6 -left-6 w-56 rounded-xl overflow-hidden border-4 border-white shadow-xl aspect-[4/3]">
              <img src={HERO.sideImage} alt="Training classroom" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -top-4 -right-4 bg-white border border-slate-200 rounded-xl shadow-lg p-4 w-52">
              <div className="flex items-center gap-2 mb-1">
                <BadgeCheck className="w-5 h-5 text-emerald-600" />
                <span className="font-display font-extrabold text-sm">Accredited RTO</span>
              </div>
              <div className="text-xs text-slate-500">Provider #45698 · Nationally recognised</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="max-w-7xl mx-auto px-4 py-20" data-testid="why-us-section">
        <div className="max-w-2xl mb-12">
          <div className="text-sm font-semibold tracking-wide uppercase text-orange-600 mb-2">Why choose us</div>
          <h2 className="text-3xl lg:text-4xl font-display font-extrabold text-[#0A2540]">Melbourne's most trusted White Card provider</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {WHY_US.map((w) => {
            const Icon = ICONS[w.icon] || ShieldCheck;
            return (
              <div key={w.title} className="wc-card group" data-testid={`why-${w.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 rounded-lg bg-[#0A2540] group-hover:bg-orange-500 transition-colors text-white flex items-center justify-center mb-4"><Icon className="w-5 h-5" /></div>
                <div className="font-display font-extrabold text-lg text-[#0A2540] mb-1">{w.title}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{w.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Course overview */}
      <section className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-sm font-semibold tracking-wide uppercase text-orange-600 mb-2">Course overview</div>
            <h2 className="text-3xl lg:text-4xl font-display font-extrabold text-[#0A2540] mb-6">{courseTitle}</h2>
            <p className="text-slate-600 leading-relaxed mb-6">{courseDescription}</p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="border-l-4 border-orange-500 pl-4"><div className="text-xs uppercase text-slate-500 font-semibold">Duration</div><div className="font-display font-extrabold text-[#0A2540]">6 hours</div></div>
              <div className="border-l-4 border-orange-500 pl-4"><div className="text-xs uppercase text-slate-500 font-semibold">Fee</div><div className="font-display font-extrabold text-[#0A2540]">{courseFee != null ? `From $${courseFee} inc GST` : "Contact us"}</div></div>
              <div className="border-l-4 border-orange-500 pl-4"><div className="text-xs uppercase text-slate-500 font-semibold">Delivery</div><div className="font-display font-extrabold text-[#0A2540]">Face to Face</div></div>
              <div className="border-l-4 border-orange-500 pl-4"><div className="text-xs uppercase text-slate-500 font-semibold">Outcome</div><div className="font-display font-extrabold text-[#0A2540]">Same day cert.</div></div>
            </div>
            <Link to="/course" className="wc-btn-secondary text-sm inline-flex items-center gap-2">View course details <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="wc-card p-8 bg-white">
            <div className="font-display font-extrabold text-xl mb-4 text-[#0A2540]">What you'll learn</div>
            <ul className="space-y-3">
              {["Identify workplace hazards and control risks","Understand WHS legislation and site responsibilities","Follow safe work procedures on any construction site","Use PPE correctly for real-world scenarios","Respond effectively to incidents and emergencies"].map((o) => (
                <li key={o} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" /><span>{o}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Upcoming classes */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-sm font-semibold tracking-wide uppercase text-orange-600 mb-2">Upcoming classes</div>
            <h2 className="text-3xl lg:text-4xl font-display font-extrabold text-[#0A2540]">Book the next available date</h2>
          </div>
          <Link to="/calendar" className="text-sm font-semibold text-[#0A2540] hover:text-orange-600 hidden md:inline-flex items-center gap-1">View full calendar <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="upcoming-classes">
          {coursesLoading && <div className="wc-card col-span-full text-center text-slate-500">Loading upcoming classes...</div>}
          {!coursesLoading && coursesError && <div className="wc-card col-span-full text-center text-rose-600">{coursesError}</div>}
          {!coursesLoading && !coursesError && upcoming.length === 0 && <div className="wc-card col-span-full text-center text-slate-500">No upcoming classes are currently available.</div>}
          {upcoming.map((c) => {
            const seats = Number(c.availableSeats ?? 0);
            const full = seats <= 0;
            const scheduleId = c.courseScheduleId;
            return (
              <div key={scheduleId} className={`wc-card flex flex-col ${full ? "opacity-60" : ""}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{new Date(c.scheduleDate).toLocaleDateString("en-AU", { weekday: "long" })}</span>
                  <span className={`wc-badge ${full ? "bg-rose-50 text-rose-700 border border-rose-200" : seats < 5 ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
                    {full ? "Full" : seats > 10 ? "Available" : `${seats} ${seats === 1 ? "seat" : "seats"} left`}
                  </span>
                </div>
                <div className="text-2xl font-display font-extrabold text-[#0A2540]">{new Date(c.scheduleDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</div>
                <div className="text-sm text-slate-600 mt-1">{formatTime(c.startTime)} - {formatTime(c.endTime)}</div>
                <div className="mt-3 pt-3 border-t border-slate-100 text-sm">
                  <div className="font-semibold text-[#0A2540] mb-1">{c.courseCode} · {c.courseName}</div>
                  <div className="flex items-center gap-1.5 text-slate-700"><MapPin className="w-3.5 h-3.5 text-orange-500" /><span className="font-semibold">{c.campusName || "Campus TBA"}</span></div>
                </div>
                <Link to={`/checkout/${scheduleId}`} state={{ selectedCourse: c }} className={`mt-4 text-sm font-semibold text-center py-2.5 rounded-lg transition-colors ${full ? "bg-slate-100 text-slate-400 pointer-events-none" : "bg-[#0A2540] text-white hover:bg-orange-500"}`} data-testid={`book-class-${scheduleId}`}>
                  {full ? "Sold out" : `Book · $${c.courseFee}`}
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-sm font-semibold tracking-wide uppercase text-orange-600 mb-2">White Card benefits</div>
          <h2 className="text-3xl lg:text-4xl font-display font-extrabold text-[#0A2540] mb-6">Your ticket to every construction site in Australia</h2>
          <p className="text-slate-600 leading-relaxed mb-6">A White Card is legally required to work on any construction site nationally. It's the first step in your construction career - and once earned, it never expires (as long as you're active in the industry).</p>
        </div>
        <div className="grid gap-3">
          {BENEFITS.map((b) => (
            <div key={b} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center shrink-0"><CheckCircle2 className="w-4 h-4" /></div>
              <span className="text-slate-700 text-sm font-medium">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="text-sm font-semibold tracking-wide uppercase text-orange-600 mb-2">FAQ</div>
            <h2 className="text-3xl lg:text-4xl font-display font-extrabold text-[#0A2540]">Frequently asked questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2" data-testid="home-faq">
            {FAQ_ITEMS.slice(0, 5).map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white border border-slate-200 rounded-lg px-4">
                <AccordionTrigger className="text-left font-semibold text-[#0A2540] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-6">
            <Link to="/faq" className="text-sm font-semibold text-[#0A2540] hover:text-orange-600 inline-flex items-center gap-1">See all FAQs <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="text-sm font-semibold tracking-wide uppercase text-orange-600 mb-2">Get in touch</div>
          <h2 className="text-3xl lg:text-4xl font-display font-extrabold text-[#0A2540] mb-4">Questions? We are here to help.</h2>
          <p className="text-slate-600 leading-relaxed mb-8">Booking for a team? Need help with your USI? Just want to double-check the date works? We're here - call us or send an email.</p>
          <div className="space-y-4">
            <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center"><Phone className="w-4 h-4" /></div><div><div className="font-semibold">{COMPANY.phone}</div><div className="text-xs text-slate-500">Mon-Fri, 8:30am to 5pm</div></div></div>
            <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center"><Mail className="w-4 h-4" /></div><div><div className="font-semibold">{COMPANY.email}</div></div></div>
            <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center"><MapPin className="w-4 h-4" /></div><div><div className="font-semibold">4/70 Main Street, Pakenham</div><div className="text-xs text-slate-500">VIC 3810</div></div></div>

          </div>
        </div>
        <form onSubmit={submit} className="wc-card p-8" data-testid="home-contact-form">
          <label className="wc-label">Name</label>
          <input required className="wc-input mb-4" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name" />
          <label className="wc-label">Email</label>
          <input required type="email" className="wc-input mb-4" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" />
          <label className="wc-label">Phone</label>
          <input className="wc-input mb-4" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="contact-phone" />
          <label className="wc-label">Message</label>
          <textarea required rows={4} className="wc-input mb-4 resize-none" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="contact-message" />
          <button type="submit" className="wc-btn-primary w-full justify-center" data-testid="contact-submit">Send message</button>
        </form>
      </section>
    </div>
  );
}
